import { cleanEntityItemDetails, NormalizedEntityItem } from '@/lib/edlink-utils.js';
import { Fragment } from 'react';

interface SummaryItemListProps {
  items: NormalizedEntityItem[];
}

/**
 * Groups the summary item details by dot-separated keys into a flat array and a grouped map.
 * @param details - The details to group.
 * @returns The flat array and grouped map.
 */
function groupDetailEntries(details: Record<string, string>): {
  flat: [string, string][];
  grouped: Map<string, [string, string][]>;
} {
  const flat: [string, string][] = [];
  const grouped = new Map<string, [string, string][]>();

  for (const [k, v] of Object.entries(details)) {
    const dotIdx = k.indexOf('.');

    if (dotIdx !== -1) {
      const prefix = k.slice(0, dotIdx);
      const suffix = k.slice(dotIdx + 1);

      if (!grouped.has(prefix)) {
        grouped.set(prefix, []);
      }

      grouped.get(prefix)!.push([suffix, v]);
    } else {
      flat.push([k, v]);
    }
  }

  return { flat, grouped };
}

export function SummaryItemList({ items }: SummaryItemListProps) {
  return (
    <ul className="grid grid-cols-2 gap-x-4 gap-y-1 ml-4 pb-2 text-sm text-muted-foreground">
      {items.map((item, i) => {
        const cleaned = item.details ? cleanEntityItemDetails(item.details) : null;
        const { flat, grouped } = cleaned ? groupDetailEntries(cleaned) : { flat: [], grouped: new Map() };

        return (
          <li key={i} className="list-disc list-inside">
            <span title={item.label}>{item.label}</span>
            {cleaned && (
              <div className="ml-5 mt-1 text-xs text-muted-foreground/70">
                <dl className="grid grid-cols-[auto_1fr] gap-x-1">
                  {flat.map(([k, v]) => (
                    <Fragment key={`${i}-${k}`}>
                      <dt className="font-medium">{`${k.charAt(0).toUpperCase()}${k.slice(1)}`}:</dt>
                      <dd className="truncate" title={v}>
                        {v}
                      </dd>
                    </Fragment>
                  ))}
                </dl>
                {[...grouped.entries()].map(([groupKey, entries]) => (
                  <div key={`${i}-${groupKey}`} className="mt-1">
                    <p className="font-medium">{`${groupKey.charAt(0).toUpperCase()}${groupKey.slice(1)}`}:</p>
                    <ul className="ml-3 mt-0.5 space-y-0.5">
                      {entries.map(([nestedKey, nestedVal]: [string, string]) => (
                        <li key={`${i}-${groupKey}-${nestedKey}`} className="flex gap-1">
                          <span className="font-medium">
                            {`${nestedKey.charAt(0).toUpperCase()}${nestedKey.slice(1)}`}:
                          </span>
                          <span className="truncate" title={nestedVal}>
                            {nestedVal}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            )}
          </li>
        );
      })}
    </ul>
  );
}
